import { useContext } from "react"
import { graphql } from "gatsby"
import cc from "classcat"
import { LocaleContext } from "contexts"
import VideoWithTitle from "components/molecules/VideoWithTitle"
import H3 from "components/atoms/H3"
import H2 from "components/atoms/H2"
import { Container } from "components/atoms/materials/Container"
import { formatArticles } from "utils/prismic"
import Media from "components/organisms/somebuddy/ListHorizontal"
import GDPRIcon from "images/gdpr.svg"
import Community from "images/community-shield.svg"
import { siteCountry } from "utils/env"
import TeamMember from "../components/TeamMember"
import useTranslations from "../hooks/useTranslations"
import SEO from "../components/seo"
import ImageUsAll from "../components/ImageUsAll"
import countries from "../../config/i18n"

const locales = countries[siteCountry] || countries.fi

type LocalizedField =
  | string
  | { default?: string | null; [key: string]: string | null | undefined }
  | null
  | undefined

// Resolves a value that may be a plain string or a locale-keyed object
// ({ default, fi_fi, sv_se, ... }) down to the string for the active locale.
// Locale keys use underscores because Gatsby rewrites JSON keys like
// "sv-se" to the GraphQL-safe "sv_se".
const resolveLocalizedField = (
  field: LocalizedField,
  locale: string
): string | null => {
  if (typeof field === "string") return field
  if (field && typeof field === "object") {
    const key = locale.replace("-", "_")
    return field[key] || field.default || null
  }
  return null
}

const Hero = () => {
  const { menuLinkAboutUs } = useTranslations()
  return (
    <div className="relative isolate overflow-hidden ">
      <ImageUsAll className="absolute inset-0 -z-10 h-full w-full object-cover " />
      <div className="mx-auto bg-kkm-darkgreen pt-10 lg:-mt-[9.5em] lg:bg-transparent lg:py-4">
        <div className="text-center">
          <h1 className="mb-0 whitespace-pre-line text-5xl font-bold tracking-tight text-zinc-100 sm:text-6xl lg:text-zinc-800">
            {menuLinkAboutUs}
          </h1>
        </div>
      </div>
    </div>
  )
}

const Timeline = ({ theme = "green" }) => {
  const {
    aboutUsTimeline1,
    aboutUsTimeline2,
    aboutUsTimeline3,
    aboutUsTimeline4,
  } = useTranslations()

  const themes = {
    light: {
      bg: "bg-white",
      name: "text-zinc-900",
      date: "text-kkm-darkgreen",
      border: "bg-zinc-800/10",
    },
    dark: {
      bg: "bg-zinc-800",
      name: "text-zinc-100",
      date: "text-kkm-green",
      border: "bg-zinc-300/30",
    },
    green: {
      bg: "bg-kkm-green",
      name: "text-zinc-900",
      date: "text-zinc-50",
      border: "bg-zinc-800/50",
    },
  }

  const timeline = [
    {
      name: aboutUsTimeline1,
      // description: "Description",
      date: "2017",
      dateTime: "2017",
    },
    {
      name: aboutUsTimeline2,
      // description: "Description",
      date: "2017–2018",
      dateTime: "2018",
    },

    {
      name: aboutUsTimeline3,
      // description: "Description",
      date: "2019",
      dateTime: "2019",
    },
    {
      name: aboutUsTimeline4,
      // description: "Description",
      date: "2019 ➝",
      dateTime: "2019",
    },
  ]

  return (
    <div className={cc(["py-24", themes[theme].bg])}>
      <div className="mx-auto -mt-8 max-w-7xl px-6 lg:px-8">
        <div
          className={cc([
            "mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none",
            {
              "lg:grid-cols-5": timeline.length === 5,
              "lg:grid-cols-4": timeline.length === 4,
            },
          ])}
        >
          {timeline.map((item) => (
            <div key={item.name}>
              <time
                dateTime={item.dateTime}
                className={cc([
                  "flex items-center text-sm font-semibold leading-6",
                  themes[theme].date,
                ])}
              >
                <svg
                  viewBox="0 0 4 4"
                  className="mr-4 h-1 w-1 flex-none"
                  aria-hidden="true"
                >
                  <circle cx={2} cy={2} r={2} fill="currentColor" />
                </svg>
                {item.date}
                <div
                  className={cc([
                    "absolute -ml-2 h-px w-screen -translate-x-full sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0",
                    themes[theme].border,
                  ])}
                  aria-hidden="true"
                />
              </time>
              <p
                className={cc([
                  "mb-0 mt-6 text-lg font-semibold tracking-tight",
                  themes[theme].name,
                ])}
              >
                {item.name}
              </p>
              {/* <p className="mt-1 text-base leading-7 text-zinc-600">
                {item.description}
              </p> */}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const Stats = ({ items, theme = "dark" }) => {
  const themes = {
    light: {
      bg: "bg-zinc-50",
      label: "text-zinc-800",
      value: "text-kkm-darkgreen",
      border: "border-zinc-500/50",
    },
    dark: {
      bg: "bg-zinc-900",
      label: "text-zinc-300",
      value: "text-white",
      border: "border-white/20",
    },
    green: {
      bg: "bg-kkm-green",
      label: "text-zinc-800",
      value: "text-zinc-900",
      border: "border-white/10",
    },
  }
  return (
    <div className={cc(["px-6 py-8 lg:px-8 lg:py-16", themes[theme].bg])}>
      <dl
        className={cc([
          "mx-auto mb-0 grid max-w-7xl grid-cols-1 gap-x-8 gap-y-12  sm:grid-cols-2 sm:gap-y-16 lg:px-8",
          {
            "lg:grid-cols-4": items.length === 4,
            "lg:grid-cols-3": items.length === 3,
          },
        ])}
      >
        {items.map((item, itemIdx) => (
          <div
            key={itemIdx}
            className={cc([
              "flex flex-col-reverse gap-y-1 border-l pl-6",
              themes[theme].border,
            ])}
          >
            <dt className={cc(["text-base", themes[theme].label])}>
              {item.label}
            </dt>
            <dd
              className={cc([
                "text-3xl font-semibold -tracking-normal",
                themes[theme].value,
              ])}
            >
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

const VideoSection = () => {
  const { aboutUsVideo1Link } = useTranslations()
  return (
    <div className="bg-kkm-darkgreen">
      <Container size="xs" className="py-10 lg:py-24">
        <VideoWithTitle link={aboutUsVideo1Link} />
      </Container>
    </div>
  )
}

const StatsHorizontal = ({ items = [], theme = "light" }) => {
  const styles = {
    dark: {
      border: "border-zinc-800/50",
      value: "text-zinc-400",
      label: "text-zinc-500",
    },
    light: {
      border: "border-zinc-400/50",
      value: "text-zinc-700",
      label: "text-zinc-900",
    },
  }
  return (
    <dl
      className={cc([
        "mb-0 mt-10 grid grid-cols-2 gap-8 border-t pt-10 sm:grid-cols-3",
        styles[theme].border,
      ])}
    >
      {items.map((item, itemIdx) => (
        <div key={itemIdx}>
          <dd
            className={cc([
              "text-3xl font-bold tracking-wide",
              styles[theme].value,
            ])}
          >
            {item.value}
          </dd>
          <dt
            className={cc([
              "mt-2 text-sm font-semibold leading-5",
              styles[theme].label,
            ])}
          >
            {item.label}
          </dt>
        </div>
      ))}
    </dl>
  )
}

const ContentWithVideo = ({ title, theme = "dark" }) => {
  const {
    aboutUsVideoContent1,
    aboutUsVideoContent2,
    aboutUsStats2Text1,
    aboutUsStats2Text2,
    aboutUsStats2Text3,
    aboutUsVideo2Link,
  } = useTranslations()

  const styles = {
    dark: {
      bg: "bg-zinc-800",
      text: "text-zinc-100",
      header: "light",
    },
    light: {
      bg: "bg-zinc-200",
      text: "text-zinc-800",
      header: "dark",
    },
  }

  const stats2 = [
    { label: aboutUsStats2Text1, value: "5000+" },
    { label: aboutUsStats2Text2, value: "15+" },
    { label: aboutUsStats2Text3, value: "300,000+" },
  ]

  return (
    <div className={cc([styles[theme].bg])}>
      <Container
        size="lg"
        className={cc([
          "flex flex-col space-y-2 py-16 lg:py-24",
          styles[theme].text,
        ])}
      >
        <div className="flex flex-col-reverse gap-10 lg:grid lg:grid-cols-2">
          <div className="flex flex-col">
            {/* <EmergencyUnit className="rounded-lg" /> */}
            <VideoWithTitle link={aboutUsVideo2Link} />

            <StatsHorizontal items={stats2} theme={theme} />
          </div>
          <div className="mx-auto flex w-full flex-col justify-start">
            <H3 size="large" textAlign="left" textStyle={styles[theme]}>
              {title}
            </H3>
            <div className="prose">
              <p className="mb-2 text-base leading-7">{aboutUsVideoContent1}</p>
              <p className="mb-0 text-base leading-7">{aboutUsVideoContent2}</p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

const StoryContent = ({ theme }) => {
  const { aboutUsStoryTitle, aboutUsStoryContent1, aboutUsStoryContent2 } =
    useTranslations()
  return (
    <div className="">
      <H2
        className="m-0 mb-6 whitespace-pre-line text-left font-extrabold tracking-tight"
        theme={theme}
      >
        {aboutUsStoryTitle}
      </H2>
      <p className="mb-2 text-base leading-7">{aboutUsStoryContent1}</p>
      <p className="mb-0 text-base leading-7">{aboutUsStoryContent2}</p>
    </div>
  )
}

const CommunitiesContent = ({ theme }) => {
  const { aboutUsCommunitiesTitle, aboutUsCommunitiesContent } =
    useTranslations()

  const styles = {
    dark: { icon: "text-white" },
    light: { icon: "text-zinc-800" },
    green: { icon: "text-zinc-800" },
  }
  return (
    <div>
      <div className="mb-6 flex items-start gap-4">
        <Community className={cc(["mt-2 w-12 shrink-0", styles[theme].icon])} />
        <H2
          className="m-0 whitespace-pre-line text-left font-extrabold tracking-tight"
          theme={theme}
        >
          {aboutUsCommunitiesTitle}
        </H2>
      </div>
      <p className="mb-0 text-base leading-7">{aboutUsCommunitiesContent}</p>
    </div>
  )
}

const VisionContent = ({ theme }) => {
  const {
    aboutUsVisionTitle,
    aboutUsVisionContent1,
    aboutUsVisionContent2,
    aboutUsVisionContent3,
  } = useTranslations()

  return (
    <div className="">
      <H2
        className="m-0 mb-6 whitespace-pre-line text-left font-extrabold tracking-tight"
        theme={theme}
      >
        {aboutUsVisionTitle}
      </H2>
      <p className="mb-2 text-base leading-7">{aboutUsVisionContent1}</p>
      <p className="mb-2 text-base leading-7">{aboutUsVisionContent2}</p>
      <p className="mb-0 text-base leading-7">{aboutUsVisionContent3}</p>
    </div>
  )
}

const SecurityContent = ({ theme }) => {
  const {
    aboutUsSecurityTitle,
    aboutUsSecurityContent1,
    aboutUsSecurityContent2,
  } = useTranslations()

  const styles = {
    dark: { icon: "text-white" },
    light: { icon: "text-zinc-800" },
    green: { icon: "text-zinc-800" },
  }
  return (
    <div className="">
      <div className="mb-6 flex items-start gap-5">
        <GDPRIcon className={cc(["mt-2 w-7 shrink-0", styles[theme].icon])} />
        <H2
          className="m-0 whitespace-pre-line text-left font-extrabold tracking-tight"
          theme={theme}
        >
          {aboutUsSecurityTitle}
        </H2>
      </div>

      <div className="">
        <p className="mb-2 text-base leading-7">{aboutUsSecurityContent1}</p>
        <p className="mb-0 text-base leading-7">{aboutUsSecurityContent2}</p>
      </div>
    </div>
  )
}

const Double = ({ Content1, Content2, style1 = "green", style2 = "dark" }) => {
  const styles = {
    dark: {
      bg: "bg-zinc-800",
      text: "text-zinc-200",
    },
    light: {
      bg: "bg-zinc-100",
      text: "text-zinc-800",
    },
    green: {
      bg: "bg-kkm-green",
      text: "text-zinc-800",
    },
  }
  return (
    <div className="relative">
      <div
        className={cc([
          "absolute inset-0 -z-10 hidden h-1/2 w-full overflow-hidden lg:block lg:h-full lg:w-1/2",
          styles[style1].bg,
        ])}
      />
      <div
        className={cc([
          "absolute right-0 -z-10 hidden h-1/2 w-full overflow-hidden lg:block lg:h-full lg:w-1/2",
          styles[style2].bg,
        ])}
      />
      <section className="mx-auto max-w-7xl lg:px-8">
        <div className="grid grid-cols-1 items-start gap-x-32 lg:grid-cols-2">
          <div
            className={cc([
              "px-4 py-16 sm:px-6 lg:bg-transparent lg:px-0 lg:py-24",
              styles[style1].bg,
              styles[style1].text,
            ])}
          >
            <Content1 theme={style1} />
          </div>
          <div
            className={cc([
              "px-4 py-16 sm:px-6 lg:bg-transparent lg:px-0 lg:py-24",
              styles[style2].bg,
              styles[style2].text,
            ])}
          >
            <Content2 theme={style2} />
          </div>
        </div>
      </section>
    </div>
  )
}

const Team = ({ team }) => {
  const {
    aboutUsTeamTitle,
    aboutUsTeamSubtitle,
    aboutUsTeamContent1,
    aboutUsTeamLink1,
    aboutUsTeamLink2,
  } = useTranslations()
  const { locale } = useContext(LocaleContext)

  return (
    <Container
      size="lg"
      className="flex flex-col space-y-12 bg-white py-16 lg:py-24"
    >
      <div className="mx-auto max-w-7xl text-center lg:px-8">
        <div className="mx-auto max-w-2xl">
          <H2 size="xl">{aboutUsTeamTitle}</H2>
        </div>
        <ul
          role="list"
          className="mx-auto mt-12 grid max-w-2xl grid-cols-2 gap-x-10 gap-y-10 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-4 lg:gap-x-12"
        >
          {team.map(({ node: person }) => {
            const resolvedEmail = resolveLocalizedField(person.email, locale)
            const resolvedPositionNote = resolveLocalizedField(
              person.positionNote,
              locale
            )

            return (
              <li key={person.name}>
                <TeamMember
                  name={person.name}
                  position={person.position}
                  positionNote={resolvedPositionNote}
                  image={person.image}
                  linkHref={person.linkHref}
                  linkText={person.linkText}
                  description={person.description}
                  phone={person.phone}
                  email={resolvedEmail}
                />
              </li>
            )
          })}
        </ul>
      </div>
      <div className="mx-auto">
        <H3>{aboutUsTeamSubtitle}</H3>
        <div className="prose">
          <p>{aboutUsTeamContent1}</p>
          <p>
            {aboutUsTeamLink1}
            <a href={`mailto:${locales[locale].contactEmail}`}>
              {locales[locale].contactEmail}
            </a>
            {aboutUsTeamLink2}
          </p>
        </div>
      </div>
    </Container>
  )
}
const SomeBuddyIsSometurva = ({ theme = "green" }) => {
  const {
    aboutUsSomebuddyTitle,
    aboutUsSomebuddyText1,
    aboutUsSomebuddyText2,
  } = useTranslations()

  return (
    <div className="bg-kkm-darkgreen">
      <Container size="md" className="pt-10">
        <H3 textStyle="light">{aboutUsSomebuddyTitle}</H3>
        <p className="mb-2 text-base leading-7 text-zinc-100">
          {aboutUsSomebuddyText1}
        </p>
        <p className="mb-0 text-base leading-7 text-zinc-100">
          {aboutUsSomebuddyText2}
        </p>
      </Container>
    </div>
  )
}

export default function About({ data }) {
  const {
    aboutUsStats1Text1,
    aboutUsStats1Text2,
    aboutUsStats1Text3,
    aboutUsVideoTitle,
    menuLinkAboutUs,
  } = useTranslations()

  const stats1 = [
    { label: aboutUsStats1Text1, value: "41 %" },
    { label: aboutUsStats1Text2, value: "50 %" },
    { label: aboutUsStats1Text3, value: "60 %" },
  ]

  const prismicArticles =
    data && data.allPrismicNewsArticle
      ? data.allPrismicNewsArticle
      : { edges: [] }
  const articles = formatArticles(prismicArticles)
  const team = data?.team?.edges || []
  return (
    <div className="">
      <SEO title={menuLinkAboutUs} />
      <Hero />
      {siteCountry === "se" && <SomeBuddyIsSometurva />}
      <VideoSection />
      <Stats items={stats1} theme="dark" />
      <ContentWithVideo title={aboutUsVideoTitle} theme="light" />
      <Double
        Content1={StoryContent}
        Content2={VisionContent}
        style1="light"
        style2="green"
      />
      <Timeline theme="dark" />
      <Double
        Content1={CommunitiesContent}
        Content2={SecurityContent}
        style1="green"
        style2="light"
      />
      <Team team={team} />
      <Media items={articles} allItemsLink="/media" />
    </div>
  )
}

export const pageQuery = graphql`
  query ($locale: String!) {
    team: allTeamJson {
      edges {
        node {
          name
          position
          positionNote {
            default
            fi_fi
            sv_fi
            sv_se
          }
          image
          phone
          email {
            default
            sv_se
          }
        }
      }
    }
    allPrismicNewsArticle(
      sort: { data: { published_at: DESC } }
      limit: 3
      filter: { lang: { eq: $locale } }
    ) {
      edges {
        node {
          id
          data {
            article {
              text
            }
            published_at(formatString: "DD.MM.YYYY")
            link_to_article {
              url
            }
            publisher_logo {
              url
            }
            publisher_name {
              text
            }
          }
        }
      }
    }
  }
`
